'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import {
  MapPin,
  Clock,
  Star,
  Phone,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  Calendar,
  Users,
  Car,
  Hotel,
  MessageCircle,
  Shield,
  Info,
} from 'lucide-react';
import type { TourPackage } from '../data/packagesData';
import { PICKUP_CITIES, type PickupCity } from '../data/packagesData';


interface Props {
  pkg: TourPackage;
}

export default function PackageDetailClient({ pkg }: Props) {
  const [pickupCity, setPickupCity] = useState<PickupCity>('pathankot');
  const [activeTab, setActiveTab] = useState<'itinerary' | 'inclusions' | 'faqs'>('itinerary');
  const [expandedDays, setExpandedDays] = useState<number[]>([1]);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const displayPrice = pkg.pickupPricing[pickupCity];
  const displayOriginal = Math.round(displayPrice * (100 / (100 - pkg.discount)));

  const whatsappMsg = encodeURIComponent(
    `Hi, I'm interested in the "${pkg.name}" package.\n\nPickup: ${PICKUP_CITIES.find(c => c.key === pickupCity)?.label}\nDuration: ${pkg.duration}\nPrice: ₹${displayPrice.toLocaleString('en-IN')}\n\nPlease share availability and booking details.`
  );

  const toggleDay = (day: number) => {
    setExpandedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const handleBookNow = () => {
    const bookingUrl = `/taxi-booking?package=${encodeURIComponent(pkg.name)}&pickup=${pickupCity}&price=${displayPrice}`;
    window.location.href = bookingUrl;
  };

  return (
    <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
        <Link href="/" className="hover:text-primary transition-colors">Home</Link>
        <span>/</span>
        <Link href="/tour-packages" className="hover:text-primary transition-colors">Tour Packages</Link>
        <span>/</span>
        <span className="text-foreground font-medium truncate">{pkg.name}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* LEFT COLUMN */}
        <div className="lg:col-span-2 space-y-6">
          {/* Hero Image */}
          <div className="relative h-72 md:h-96 rounded-2xl overflow-hidden shadow-lg">
            <AppImage
              src={pkg.image}
              alt={pkg.alt}
              fill
              className="object-cover"
              sizes="(max-width: 1024px) 100vw, 66vw"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <span className={`${pkg.tagColor} mb-2 inline-block`}>{pkg.tag}</span>
              <h1 className="text-2xl md:text-3xl font-extrabold text-white leading-tight">{pkg.name}</h1>
              <p className="text-white/80 text-sm mt-1 flex items-center gap-1">
                <MapPin size={13} />
                {pkg.destination}
              </p>
            </div>
          </div>

          {/* Quick Info Strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { icon: <Clock size={18} className="text-primary" />, label: 'Duration', value: pkg.duration },
              { icon: <Star size={18} className="text-accent fill-current" />, label: 'Rating', value: `${pkg.rating} (${pkg.reviews} reviews)` },
              { icon: <Calendar size={18} className="text-primary" />, label: 'Best Time', value: pkg.bestTimeToVisit },
              { icon: <Users size={18} className="text-primary" />, label: 'Travel Type', value: pkg.travelType.slice(0, 2).join(', ') },
            ].map((item) => (
              <div key={item.label} className="bg-white rounded-xl border border-border p-3 text-center">
                <div className="flex justify-center mb-1">{item.icon}</div>
                <p className="text-xs text-muted-foreground">{item.label}</p>
                <p className="text-xs font-bold text-foreground mt-0.5">{item.value}</p>
              </div>
            ))}
          </div>

          {/* Description */}
          <div className="bg-white rounded-2xl border border-border p-5">
            <h2 className="text-lg font-bold text-foreground mb-3">About This Package</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">{pkg.description}</p>

            {/* Highlights */}
            <div className="mt-4">
              <h3 className="text-sm font-bold text-foreground mb-2">Package Highlights</h3>
              <div className="flex flex-wrap gap-2">
                {pkg.highlights.map((h) => (
                  <span key={h} className="flex items-center gap-1.5 text-xs bg-primary-light text-primary px-3 py-1.5 rounded-full font-medium">
                    <CheckCircle size={11} />
                    {h}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-2xl border border-border overflow-hidden">
            <div className="flex border-b border-border">
              {(['itinerary', 'inclusions', 'faqs'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-3 text-sm font-semibold capitalize transition-colors ${
                    activeTab === tab
                      ? 'text-primary border-b-2 border-primary bg-primary-light' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab === 'itinerary' ? '📅 Itinerary' : tab === 'inclusions' ? '✅ Inclusions' : '❓ FAQs'}
                </button>
              ))}
            </div>

            <div className="p-5">
              {/* Itinerary Tab */}
              {activeTab === 'itinerary' && (
                <div className="space-y-3">
                  {pkg.itinerary.map((day) => (
                    <div key={day.day} className="border border-border rounded-xl overflow-hidden">
                      <button
                        onClick={() => toggleDay(day.day)}
                        className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {day.day}
                          </span>
                          <div className="text-left">
                            <p className="font-semibold text-foreground text-sm">{day.title}</p>
                            {day.distance && (
                              <p className="text-xs text-muted-foreground">{day.distance} • {day.travelTime}</p>
                            )}
                          </div>
                        </div>
                        {expandedDays.includes(day.day) ? (
                          <ChevronUp size={16} className="text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronDown size={16} className="text-muted-foreground flex-shrink-0" />
                        )}
                      </button>
                      {expandedDays.includes(day.day) && (
                        <div className="px-4 pb-4 border-t border-border bg-muted/20">
                          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{day.description}</p>
                          {day.sightseeing && day.sightseeing.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs font-semibold text-foreground mb-1.5">🏛 Sightseeing:</p>
                              <div className="flex flex-wrap gap-1.5">
                                {day.sightseeing.map((s) => (
                                  <span key={s} className="text-xs bg-white border border-border px-2 py-0.5 rounded-full text-muted-foreground">{s}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {day.meals && (
                            <p className="text-xs text-muted-foreground mt-2">🍽 Meals: {day.meals}</p>
                          )}
                          {day.overnight && (
                            <p className="text-xs text-muted-foreground mt-1">🏨 Overnight: {day.overnight}</p>
                          )}
                          {day.notes && (
                            <div className="mt-2 flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-lg p-2">
                              <Info size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
                              <p className="text-xs text-amber-700">{day.notes}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Inclusions Tab */}
              {activeTab === 'inclusions' && (
                <div className="space-y-5">
                  <div>
                    <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-500" />
                      Inclusions
                    </h3>
                    <div className="space-y-2">
                      {pkg.includes.map((item) => (
                        <div key={item} className="flex items-center gap-2.5 text-sm text-foreground">
                          <CheckCircle size={14} className="text-green-500 flex-shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="border-t border-border pt-5">
                    <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                      <XCircle size={16} className="text-red-500" />
                      Exclusions
                    </h3>
                    <div className="space-y-2">
                      {pkg.excludes.map((item) => (
                        <div key={item} className="flex items-center gap-2.5 text-sm text-muted-foreground">
                          <XCircle size={14} className="text-red-400 flex-shrink-0" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                  {pkg.vehicleDetails && (
                    <div className="border-t border-border pt-5">
                      <h3 className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
                        <Car size={16} className="text-primary" />
                        Vehicle Details
                      </h3>
                      <p className="text-sm text-muted-foreground">{pkg.vehicleDetails}</p>
                    </div>
                  )}
                  {pkg.hotelDetails && (
                    <div className="border-t border-border pt-5">
                      <h3 className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
                        <Hotel size={16} className="text-primary" />
                        Hotel Details
                      </h3>
                      <p className="text-sm text-muted-foreground">{pkg.hotelDetails}</p>
                    </div>
                  )}
                  <div className="border-t border-border pt-5">
                    <h3 className="text-sm font-bold text-foreground mb-2 flex items-center gap-2">
                      <Shield size={16} className="text-primary" />
                      Booking & Cancellation Policy
                    </h3>
                    <p className="text-sm text-muted-foreground mb-2">{pkg.bookingPolicy}</p>
                    <p className="text-sm text-muted-foreground">{pkg.cancellationPolicy}</p>
                  </div>
                </div>
              )}

              {/* FAQs Tab */}
              {activeTab === 'faqs' && (
                <div className="space-y-3">
                  {pkg.faqs.map((faq, i) => (
                    <div key={i} className="border border-border rounded-xl overflow-hidden">
                      <button
                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors text-left"
                      >
                        <span className="font-semibold text-foreground text-sm pr-4">{faq.q}</span>
                        {openFaq === i ? (
                          <ChevronUp size={16} className="text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronDown size={16} className="text-muted-foreground flex-shrink-0" />
                        )}
                      </button>
                      {openFaq === i && (
                        <div className="px-4 pb-4 border-t border-border bg-muted/20">
                          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Back Link */}
          <Link
            href="/tour-packages"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft size={16} />
            Back to All Packages
          </Link>
        </div>

        {/* RIGHT COLUMN — Sticky Booking Card */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 space-y-4">
            {/* Price Card */}
            <div className="bg-white rounded-2xl border border-border shadow-lg p-5">
              <h3 className="text-base font-bold text-foreground mb-4">Book This Package</h3>

              {/* Pickup City */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-foreground mb-2 block flex items-center gap-1">
                  <MapPin size={12} className="text-primary" />
                  Select Pickup City
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PICKUP_CITIES.map((city) => (
                    <button
                      key={city.key}
                      onClick={() => setPickupCity(city.key)}
                      className={`py-2 px-3 rounded-xl text-xs font-semibold border-2 transition-all duration-150 ${
                        pickupCity === city.key
                          ? 'border-primary bg-primary text-white' :'border-border text-muted-foreground hover:border-primary hover:text-primary'
                      }`}
                    >
                      {city.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Display */}
              <div className="bg-primary-light rounded-xl p-4 mb-4">
                <p className="text-xs text-muted-foreground mb-1">Starting from</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-extrabold text-primary font-tabular">
                    ₹{displayPrice.toLocaleString('en-IN')}
                  </span>
                  <span className="text-sm text-muted-foreground line-through font-tabular">
                    ₹{displayOriginal.toLocaleString('en-IN')}
                  </span>
                  <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                    {pkg.discount}% OFF
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Per vehicle from {PICKUP_CITIES.find(c => c.key === pickupCity)?.label}
                </p>
              </div>

              {/* Duration */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Clock size={14} className="text-primary" />
                <span>{pkg.duration} • {pkg.durationDays} Days / {pkg.durationNights} Nights</span>
              </div>

              {/* CTAs */}
              <div className="space-y-3">
                <button
                  onClick={handleBookNow}
                  className="w-full btn-primary py-3 text-base justify-center"
                >
                  Book Now
                </button>
                <a
                  href={`https://wa.me/918894915859?text=${whatsappMsg}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-green-500 text-white font-bold py-3 rounded-xl transition-colors"
                >
                  <MessageCircle size={18} />
                  WhatsApp Enquiry
                </a>
                <a
                  href="tel:+918894915859"
                  className="w-full flex items-center justify-center gap-2 border-2 border-border text-foreground font-semibold py-3 rounded-xl hover:border-primary hover:text-primary transition-colors"
                >
                  <Phone size={16} />
                  Call: +91 88949 15859
                </a>
              </div>

              {/* Trust Badges */}
              <div className="mt-4 pt-4 border-t border-border space-y-2">
                {[
                  '✅ Instant Booking Confirmation',
                  '🔒 Secure Online Payment',
                  '🚗 Verified & Experienced Drivers',
                  '📞 24×7 Customer Support',
                ].map((badge) => (
                  <p key={badge} className="text-xs text-muted-foreground">{badge}</p>
                ))}
              </div>
            </div>

            {/* Pickup Pricing Table */}
            <div className="bg-white rounded-2xl border border-border p-4">
              <h4 className="text-sm font-bold text-foreground mb-3">Pricing by Pickup City</h4>
              <div className="space-y-2">
                {PICKUP_CITIES.map((city) => (
                  <div key={city.key} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{city.label}</span>
                    <span className="font-bold text-primary font-tabular">
                      ₹{pkg.pickupPricing[city.key].toLocaleString('en-IN')}
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                * Prices are per vehicle. Contact us for group discounts.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
